import { cn } from '@/lib/utils';

/**
 * Which store(s) a Product Details field's value reaches.
 *
 *  - `tenant`   — one value on `products`, shared by every location.
 *  - `location` — written per location (`product_prices`, `product_locations`,
 *                 `product_payees`). Switching the header location switches
 *                 which row you're editing.
 *  - `override` — a tenant default a location may override. The unit selectors
 *                 read as one control but persist to `product_locations` once a
 *                 location is active.
 *
 * Scope is homogeneous per card on that page, so the chip goes on the card
 * header and covers every field inside it; only fields whose scope disagrees
 * with their card get their own marker. Marking all sixty inputs would restate
 * a fact nobody disputes sixty times.
 *
 * Lives in lib/ rather than beside the components so the form, the products
 * table and bulk edit can all read one definition — the answer can't drift.
 */
export type FieldScope = 'tenant' | 'location' | 'override';

/**
 * Classes for a section card. Location-scoped cards get a violet left rail;
 * tenant cards get nothing extra, because the rail is the signal — stamping
 * one on every card would turn it into decoration.
 *
 * Pair with a `data-scope` attribute: that drives spotlight mode in index.css
 * and gives tests something stable to assert on.
 */
export function scopeCardCls(scope: FieldScope, className?: string) {
  return cn(
    'relative rounded-lg border bg-white p-6',
    scope === 'location'
      ? 'border-violet-200 border-l-[3px] border-l-violet-600 pl-[calc(1.5rem+1px)]'
      : 'border-border',
    className,
  );
}

/** Distinct non-empty values in a list, for the "N values across M locations" hints. */
export function distinctValues(values: (string | null | undefined)[]): string[] {
  return Array.from(new Set(values.map((v) => (v ?? '').trim()).filter((v) => v !== '')));
}
